import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import moment from 'moment';
import InsightsReportCard from '@intelligence/components/ReportCard/InsightsReportCard';
import NoData from '@intelligence/components/CommonComponents/NoData';
import { IS_WOA } from '@home/_config/config';
import { splitStringByChars } from '@home/utils/utils';
import { highlightToHtml } from '@intelligence/components/HighlightText';
import { getReportTypeConfig } from '@intelligence/_config/reportConfig';
import type { IntelligenceReportDataInfo } from '../../types';
import type { TReportConfigMap } from '@intelligence/utils/reportConfig';
import { resolveReportConfig } from '@intelligence/utils/reportConfig';
import type { TReportSearchError } from '../../hooks/types';
import { isDatasourceReport, getDatasourceDisplay } from '../../utils';
import AiSearchLoading from '../AiSearchLoading';
import { buildDatasourceAuthor, buildReportAuthors } from '@intelligence/components/ReportCard/authors';

interface ISearchContentProps {
  loading: boolean;
  error: TReportSearchError;
  results: IntelligenceReportDataInfo[];
  reportSourceConfig: TReportConfigMap;
  searchKeyword: string;
  isEn: boolean;
  onLikeChange: (resourceId: string, likeCounts?: number) => void;
}

/**
 * 将搜索结果转换为 InsightsReportCard 所需的数据格式
 * 使用 splitStringByChars 确保 pipe-separated 的 tag 被正确分割
 */
const convertToCardData = (
  report: IntelligenceReportDataInfo,
  searchKeyword: string,
  reportSourceConfig: TReportConfigMap,
) => {
  const keyword = searchKeyword?.trim();
  const rawTagEn = (report.tag_en || []).flatMap(splitStringByChars);
  const rawTagCn = (report.tag || []).flatMap(splitStringByChars);

  const isDatasource = isDatasourceReport(report.system);
  const { display_source: displaySource } = resolveReportConfig(report, reportSourceConfig);
  const sourceConfig = isDatasource
    ? getDatasourceDisplay(displaySource)
    : null;

  const cardData = {
    id: report.id,
    resource_id: report.resource_id,
    title: keyword
      ? [highlightToHtml(report.title_en, keyword), highlightToHtml(report.title, keyword)]
      : [report.title_en, report.title],
    summary: keyword
      ? [highlightToHtml(report.introduction_en, keyword), highlightToHtml(report.introduction, keyword)]
      : [report.introduction_en, report.introduction],
    tag: keyword
      ? [
        rawTagEn.map(tag => highlightToHtml(tag, keyword)),
        rawTagCn.map(tag => highlightToHtml(tag, keyword)),
      ]
      : [rawTagEn, rawTagCn],
    coverImg: report.cover,
    authors: isDatasource
      ? [buildDatasourceAuthor(sourceConfig?.name || '', sourceConfig?.img || '')]
      : buildReportAuthors(report, { textOrder: 'en-cn' }),
    time: moment(report.create_time).format('YYYY-MM-DD'),
    report_type: [report.report_type_en, report.report_type],
    watched_cnt: report.watched_cnt,
    report_like: report.report_like,
  };
  return cardData;
};

const SearchContent: React.FC<ISearchContentProps> = ({
  loading,
  error,
  results,
  reportSourceConfig,
  searchKeyword,
  isEn,
  onLikeChange,
}) => {
  const { formatMessage } = useIntl();

  const cardDataList = useMemo(
    () => results.map(r => ({
      report: r,
      cardData: convertToCardData(r, searchKeyword, reportSourceConfig),
    })),
    [results, searchKeyword, reportSourceConfig],
  );

  if (loading) {
    return (
      <div className="dbi-report-search__results">
        <AiSearchLoading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dbi-report-search__results">
        <div className="dbi-report-search__empty-wrapper">
          <div className="dbi-report-search__section-empty">
            {formatMessage({ id: 'dbi_common_operation_failed_tips' })}
          </div>
        </div>
      </div>
    );
  }

  if (results.length > 0) {
    return (
      <div className="dbi-report-search__results">
        <div className="dbi-report-search__card-list">
          {cardDataList.map(({ report, cardData }) => (
            <InsightsReportCard
              key={report.resource_id}
              data={cardData as any}
              lang={isEn ? 0 : 1}
              shareBack=""
              reportTypeConfig={getReportTypeConfig(report.system)}
              pdfPreviewDownloadPage="/pdfPreviewDownloadLikeShare"
              onChange={onLikeChange}
              showContactInfo={!IS_WOA && !isDatasourceReport(report.system)}
              {...{ needPopover: true } as any}
              reportLangSegment=""
              isNew={!!moment(report.create_time).isAfter(moment().subtract(1, 'month'))}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="dbi-report-search__results">
      <div className="dbi-report-search__empty-wrapper">
        <NoData />
      </div>
    </div>
  );
};

export default React.memo(SearchContent);
